import type { useTranslations } from "next-intl";
import type { ValidationMessages, UTMParams } from "./types";
import { getUTMs } from "@/app/_analytics/utmHandler";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";

type ValidationTranslationFn = ReturnType<typeof useTranslations<"ClientKeys.forms.validation">>;

/** Build a ValidationMessages object from the shared `ClientKeys.forms.validation` namespace. */
export const createValidationMessages = (t: ValidationTranslationFn): ValidationMessages => ({
  required: t("required"),
  email: t("email"),
  url: t("url"),
  phone: t("phone"),
  termsRequired: t("required"),
  maxLength: (max: number) => t("max_length", { max }),
});

// ---------------------------------------------------------------------------
// UTM enrichment
// ---------------------------------------------------------------------------

/** Appends UTM params (from localStorage) to any form values object. */
export function enrichWithUTMs<T extends Record<string, unknown>>(formValues: T): T & UTMParams {
  const utms = getUTMs();
  return {
    ...formValues,
    utm_source: utms.utmSource,
    utm_medium: utms.utmMedium,
    utm_campaign: utms.utmCampaign,
    utm_content: utms.utmContent,
    utm_term: utms.utmTerm,
  };
}

// ---------------------------------------------------------------------------
// Content gate (localStorage-based unlock tracking)
// ---------------------------------------------------------------------------

const getUnlockedContentGates = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const stored = safeLocalStorage.getItem(LocalStorageKeys.CONTENT_GATE_UNLOCKED);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Check if a content gate has been submitted/unlocked. */
export const isContentGateSubmitted = (formId: string): boolean => {
  return getUnlockedContentGates().includes(formId);
};

/**
 * Mark a content gate as submitted/unlocked.
 * Silently fails if localStorage is unavailable.
 */
export const setContentGateSubmitted = (formId: string): void => {
  if (typeof window === "undefined") return;
  const unlocked = getUnlockedContentGates();
  if (!unlocked.includes(formId)) {
    unlocked.push(formId);
    safeLocalStorage.setItem(LocalStorageKeys.CONTENT_GATE_UNLOCKED, JSON.stringify(unlocked));
  }
};

/** Clear all unlocked content gates (useful for testing/debugging). */
export const clearAllContentGates = (): void => {
  if (typeof window === "undefined") return;
  safeLocalStorage.removeItem(LocalStorageKeys.CONTENT_GATE_UNLOCKED);
};
